import { colorMap, EXECUTIVE_BRIEF, LEARN_MORE_LINK, NETAPP_LOGO_BASE64, statusMap } from './pdfConstants';

/**
 * Builds the PDF definition object for agent deployment report.
 * @param {Object} json - The report data.
 * @param {Function} t - Translation function for i18n labels.
 * @returns {Object} PDFMake document definition.
 */

export function buildPdfDefinitionFromJson(json, t, agentId, agentName) {
  // Localized labels for all report fields using i18n keys
  const labels = {
    state: t('pdf_text_state'),
    startTime: t('pdf_text_startTime'),
    agentId: t('pdf_text_agent_id'),
    agentName: t('pdf_text_agent_name'),
    endTime: t('pdf_text_endTime'),
    duration: t('pdf_text_duration'),
    networkEndpoint: t('pdf_text_network_endpoint'),
    portsHeader: t('pdf_text_header_ports'),
    mountPath: t('pdf_text_hardware_mount_path'),
    result: t('pdf_text_result'),
    status: t('pdf_text_status'),
    service: t('pdf_text_service'),
    serviceStatus: t('pdf_text_serviceStatus'),
    validation: t('pdf_text_validation'),
    validationNetwork: t('pdf_text_network'),
    validationHardware: t('pdf_text_hardware'),
    validationPorts: t('pdf_text_ports'),
    summary: t('pdf_text_summary'),
    details: t('pdf_text_details'),
    reportTitle: t('pdf_text_reportTitle'),
    executiveBrief: t('pdf_text_executive_brief'),
    troubleshooting: t('pdf_text_troubleshooting'),
    reportDescription: t('pdf_text_reportDescription')
  };

    const logoConfig = {
    image: NETAPP_LOGO_BASE64,
    width: 80,
    height: 14,
    alignment: 'left',
    margin: [0, 0, 0, 20]
  };

  const executiveBrief = {
    margin: [0, 0, 0, 20],
    stack: [
      {
        text: labels.reportDescription + ' ',
        fontSize: 11,
        margin: [0, 0, 0, 6]
      },
      {
        ul: [
           { text: [ 'See ', { text: 'Summary', bold: true }, ' for overall status.' ] },
           { text: [ 'If the ', { text: 'State', bold: true }, ' is ', { text: 'Failure', bold: true }, ', the deployment did not complete.' ] },
           {
            ul:[
              { text: [ 'Go to ', { text: 'Details', bold: true }, ' for more information.' ] },
              { text: [ 'Fix any failed checks, then try deploying again.' ] }
            ]
          },
        ],
        fontSize: 11,
        margin: [12, 0, 0, 0]
      },
      {
          text: labels.troubleshooting,
          link: LEARN_MORE_LINK,
          color: '#1a73e8',
          decoration:'underline',
          margin: [0, 10, 0, 6]
      },
    ]
  };


  /**
   * Formats a date string to a readable UTC format.
   * @param {string|Date} dt
   * @returns {string}
   */
  function formatDate(dt) {
    if (!dt) return '-';
    const date = new Date(dt);
    if (isNaN(date)) return '-';
    return date.toLocaleString('en-US', { timeZone: 'UTC', hour12: true }) + ' UTC';
  }
  
  /**
   * Formats a duration in milliseconds to seconds, or returns '-' if invalid.
   * @param {number} ms
   * @returns {string}
   */
  function formatDuration(ms) {
    if (typeof ms !== 'number' || isNaN(ms) || ms < 0) {
      return '-';
    }
    const secs = Math.floor(ms / 1000);
    const unit = secs === 1 ? 'second' : 'seconds';
    return `${secs} ${unit}`;
  }
  
  /**
   * Returns a styled status cell for the PDF table.
   * @param {string} status
   * @returns {Object}
   */
  function getStatusText(status) {
    if (!status) return { text: '-', alignment: 'center' };
    const key = typeof status === 'string' ? status.trim().toUpperCase() : status;
    const base = statusMap[key];
    if (base) {
      return { ...base };
    }
    return { text: String(status), alignment: 'center' };
  }
  
  /**
   * Creates a table cell with optional alignment.
   * @param {any} value
   * @param {string} alignment
   * @returns {Object}
   */
  function createCell(value, alignment = 'left') {
    if (value && typeof value === 'object' && value.text !== undefined) {
      return { ...value, alignment };
    }
    return { text: value ?? '-', alignment };
  }
  
  /**
   * Builds the summary table for the report.
   * Handles missing/invalid start/end times gracefully.
   * @returns {Object}
   */
  function getSummaryTable() {
    const startTimeValid = json?.startTime && !isNaN(new Date(json.startTime));
    const endTimeValid = json?.endTime && !isNaN(new Date(json.endTime));
    const startTime = startTimeValid ? formatDate(json.startTime) : '-';
    const endTime = endTimeValid ? formatDate(json.endTime) : '-';
    let duration = '-';
    if (startTimeValid && endTimeValid) {
      duration = formatDuration(new Date(json.endTime) - new Date(json.startTime));
    }
    
    let summaryRows = [
      { label: labels.state, value: getStatusText(json?.state), alignment: 'left' },
      { label: labels.startTime, value: startTime },
      { label: labels.endTime, value: endTime },
      { label: labels.duration, value: duration === "0 seconds" ? "less than 1 second" : duration },
    ];

    if (agentId || agentName) {
      summaryRows = [{ label: labels.agentName, value: agentName }, { label: labels.agentId, value: agentId }, ...summaryRows];
    }

    return {
      table: {
        widths: ['auto', '*'],
        body: summaryRows.map(row => [
          { text: row.label, bold: true, fillColor: colorMap.blue, color: colorMap.black },
          createCell(row.value, row.alignment)
        ])
      },
      layout: {
        hLineColor: () => colorMap.lightGray,
        vLineColor: () => colorMap.lightGray,
        hLineWidth: () => 1,
        vLineWidth: () => 1
      },
      margin: [0, 0, 0, 20],
    };
  }

  /**
   * Builds a validation table for a given validation object.
   * Only the first object in check.data is used for the Data column.
   * @param {Object} validation
   * @returns {Object}
   */
  function getValidationTable(validation) {
    let headerText = '';
    if (agentId) {
      if (validation.type === "NETWORK") {
        headerText = labels.networkEndpoint;
      } else if (validation.type === "HARDWARE") {
        headerText = labels.mountPath;
      } else if (validation.type === "PORTS") {
        headerText = labels.portsHeader;
      }
    }

    return {
      table: {
        headerRows: 1,
        widths: ['80%', '20%'],
        body: [
          [
            { text: agentId ? headerText : labels.networkEndpoint, bold: true, fillColor: colorMap.blue, color: colorMap.black },
            { text: labels.result, bold: true, fillColor: colorMap.blue, color: colorMap.black, alignment: 'center' },
          ],
          ...((validation?.checks || []).map(check => [
            {
              text: check.data && check.data.length > 0
                ? check.data[0].value // Only use the first object for now
                : '-',
              color: colorMap.black,
            },
           getStatusText(check.status),
          ]))
        ]
      },
      layout: {
        fillColor: rowIndex => rowIndex === 0 ? colorMap.blue : null,
        hLineColor: () => colorMap.lightGray,
        vLineColor: () => colorMap.lightGray,
        hLineWidth: () => 1,
        vLineWidth: () => 1,
      },
      margin: [0, 0, 0, 10],
    };
  }
  
  /**
   * Builds the section for each service, including all its validations.
   * @param {Object} service
   * @returns {Array}
   */
  function getServiceSection(service, agentId) {
    if (!service) return [];
    const content = [];
    (service.validations || []).forEach(validation => {
      let headerText = '';
      if (agentId) {
        if (validation.type === "NETWORK") {
          headerText = labels.validationNetwork;
        } else if (validation.type === "HARDWARE") {
          headerText = labels.validationHardware;
        } else if (validation.type === "PORTS") {
          headerText = labels.validationPorts;
        }
      }
      content.push(
        { text: agentId ? headerText : `${validation.type ?? '-'} ${labels.validation}`, style: 'subheader' },
        {
          text: [
            { text: `${labels.status}: ` },
            getStatusText(validation.validationStatus)
          ],
          margin: [0, 0, 0, 6],
        },
        getValidationTable(validation, agentId)
      );
    });
    return content;
  }

  // Check if there are any services to display in the details section
  const hasServices = Array.isArray(json?.services) && json.services.length > 0;
  // Build and return the PDFMake document definition
  return {
    content: [
      logoConfig,
      { text: labels.reportTitle, style: 'mainTitle', margin: [0, 0, 0, 18] },
      { text: labels.executiveBrief, style: 'sectionTitle', margin: [0, 0, 0, 8]},
      executiveBrief,
      { text: labels.summary, style: 'sectionTitle', margin: [0, 0, 0, 8] },
      getSummaryTable(),
      ...(hasServices
        ? [
            { text: labels.details, style: 'sectionTitle', margin: [0, 0, 0, 8] },
            ...json.services.flatMap((service, idx, arr) => [
              ...getServiceSection(service, agentId),
              idx < arr.length - 1
                ? {
                    canvas: [
                      { type: 'line', x1: 0, y1: 0, x2: 520, y2: 0, lineWidth: 2, lineColor: colorMap.lightGray }
                    ],
                    margin: [0, 15, 0, 15],
                  }
                : []
            ])
          ]
        : [])
    ],
    styles: {
      mainTitle: { fontSize: 18, bold: true, color: colorMap.black, alignment: 'center', margin: [0, 0, 0, 18] },
      sectionTitle: { fontSize: 15, bold: true, margin: [0, 10, 0, 10] },
      serviceHeader: { fontSize: 14, bold: true, margin: [0, 2, 0, 6] },
      subheader: { fontSize: 12, bold: true, margin: [0, 8, 0, 4] },
    },
    defaultStyle: {
      fontSize: 10,
    },
  };
}